import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

// Custom component to provide container context for proper portal rendering
const PopoverPortal = ({ 
  container, 
  children 
}: { 
  container?: HTMLElement | null;
  children: React.ReactNode 
}) => {
  return (
    <PopoverPrimitive.Portal container={container}>
      {children}
    </PopoverPrimitive.Portal>
  )
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    container?: HTMLElement | null;
  }
>(({ className, align = "center", sideOffset = 4, container, ...props }, ref) => {
  // Find the nearest dialog or sheet container
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  
  React.useEffect(() => {
    // If a container is explicitly provided, use it
    if (container) {
      setPortalContainer(container);
      return;
    }
    
    // Otherwise try to find the nearest dialog or sheet container
    const dialogContainer = document.querySelector('[role="dialog"]') as HTMLElement;
    if (dialogContainer) {
      setPortalContainer(dialogContainer);
    }
  }, [container]);

  return (
    <PopoverPortal container={portalContainer}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-[100] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        // Only prevent default for escape key in specific scenarios
        onEscapeKeyDown={(e) => {
          const activeElement = document.activeElement;
          if (activeElement instanceof HTMLElement && 
              (activeElement.tagName === 'INPUT' || 
               activeElement.tagName === 'TEXTAREA' ||
               activeElement.getAttribute('contenteditable') === 'true')) {
            // Only prevent default if we're in an input field to avoid trapping focus
            e.preventDefault();
          }
        }}
        
        // Updated to close when clicking outside
        onPointerDownOutside={(e) => {
          // Only prevent closing when clicking on another popover or dialog
          if (e.target instanceof Node) {
            const target = e.target as HTMLElement;
            if (target.closest('[data-radix-popover-content]') || 
                target.closest('[role="dialog"]')) {
              e.preventDefault();
            }
          }
        }}
        
        // Allow all interactions within the popover
        onInteractOutside={(e) => {
          // Only prevent closing when interacting with another popover or dialog
          if (e.target instanceof Node) {
            const target = e.target as HTMLElement;
            if (target.closest('[data-radix-popover-content]') || 
                target.closest('[role="dialog"]')) {
              e.preventDefault();
            }
          }
        }}
        
        // Let events flow naturally in the popover
        {...props}
      />
    </PopoverPortal>
  );
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }
